import type { ReactNode } from "react";

import {
  usePermissions,
  type PermissionAction,
  type PermissionResource,
} from "@/hooks/usePermissions";
import type { OrganisationRole } from "@/types/portal";

type ReadOnlyProps = {
  /** Content to wrap */
  children: ReactNode;
} & (
  | {
      /** Roles that can edit (if user doesn't have these, content becomes read-only) */
      editRoles: OrganisationRole[];
      action?: never;
      resource?: never;
    }
  | {
      /** Action required to edit */
      action: PermissionAction;
      /** Resource to check */
      resource: PermissionResource;
      editRoles?: never;
    }
);

/**
 * Wrap content in a read-only state based on permissions.
 * Sets pointer-events to none and reduces opacity when user can't edit.
 *
 * @example
 * ```tsx
 * <ReadOnly editRoles={["org_owner", "org_admin"]}>
 *   <SettingsForm />
 * </ReadOnly>
 *
 * <ReadOnly action="edit" resource="settings">
 *   <SettingsForm />
 * </ReadOnly>
 * ```
 */
export function ReadOnly({
  children,
  editRoles,
  action,
  resource,
}: ReadOnlyProps) {
  const { can, hasRole } = usePermissions();

  let canEdit = false;

  if (editRoles) {
    canEdit = hasRole(...editRoles);
  } else if (action && resource) {
    canEdit = can(action, resource);
  }

  if (canEdit) {
    return <>{children}</>;
  }

  return (
    <div
      className="pointer-events-none opacity-60"
      aria-disabled="true"
      role="group"
      aria-label="Read-only content"
    >
      {children}
    </div>
  );
}

/**
 * Read-only badge to show when content is read-only.
 */
export function ReadOnlyBadge({ show }: { show: boolean }) {
  if (!show) return null;

  return (
    <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
      Read only
    </span>
  );
}
